import { useCallback } from 'react';
import { saveAs } from 'file-saver';
import usePhotoDownloadLogger from '../../Stats/components/hooks/usePhotoDownloadLogger';

interface ShareSheetProps {
    uri: string;
    mimeType: string;
    mediaId: number;
    source: 'gallery' | 'personal-gallery' | 'share';
    children: React.ReactNode;
}

const MIME_LOOKUP: { [key: string]: string } = {
    'video/mp4': 'mp4',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/png': 'png',
};

const getFileName = (url: string, mimeType: string): string => {
    const basename = url.split('/').reverse()[0];
    const extension = MIME_LOOKUP[mimeType];
    if (basename.split('.').length === 1) {
        return `${basename}.${extension}`;
    }
    return basename;
};

export default function ShareSheet({
    uri,
    mimeType,
    mediaId,
    source,
    children,
}: ShareSheetProps) {
    const { logPhotoDownload } = usePhotoDownloadLogger();

    const download = useCallback(
        async (e: any) => {
            e.stopPropagation();

            logPhotoDownload(mediaId, source);

            const canShare = !!navigator.canShare;
            console.log(`Share Check: ${canShare}`);

            if (canShare) {
                try {
                    const response = await fetch(uri, {
                        method: 'GET',
                    });

                    console.log(
                        `${mediaId}${
                            mediaId.toString().split('.').length === 1
                                ? `.${MIME_LOOKUP[mimeType]}`
                                : ''
                        }`,
                    );

                    const file = new File(
                        [await response.blob()],
                        `${mediaId}${
                            mediaId.toString().split('.').length === 1
                                ? `.${MIME_LOOKUP[mimeType]}`
                                : ''
                        }`,
                    );

                    const files = [file];
                    const shareData = {
                        files: files,
                    };

                    if (navigator?.canShare(shareData)) {
                        console.log('Share allowed');
                        try {
                            await navigator.share(shareData);
                        } catch (e) {
                            console.log('Share failed', e);
                        }

                        return;
                    }
                } catch (e) {
                    console.log('Share failed', e);
                }
            }

            console.log('Fallback to download');
            try {
                fetch(uri, {
                    method: 'GET',
                })
                    .then((response) => {
                        return response.blob();
                    })
                    .then((blob: Blob) => {
                        saveAs(blob, getFileName(uri, mimeType));
                    })
                    .catch(() => {
                        saveAs(uri, getFileName(uri, mimeType));
                    });
            } catch (e) {
                console.log('Download failed', e);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [uri],
    );

    return (
        <div onClick={download} className={'relative'}>
            {children}
        </div>
    );
}
